"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSlotApiPayloadSchema = exports.assistantPlannedSlotApiResponseSchema = exports.overrideSlotApiPayloadSchema = exports.overrideSlotAbsencePayloadSchema = exports.overrideSlotCancelPayloadSchema = exports.overrideSlotReassignPayloadSchema = exports.overrideSlotMovePayloadSchema = exports.timeRangesSchema = exports.SLOT_NOTE_MAX_LENGTH = void 0;
const zod_1 = require("zod");
const absence_enums_1 = require("./absence-enums");
const enums_1 = require("./enums");
const slot_types_1 = require("./slot-types");
/** Max length of a staff note left on a slot edit. */
exports.SLOT_NOTE_MAX_LENGTH = 1000;
const slotNoteSchema = zod_1.z.string().max(exports.SLOT_NOTE_MAX_LENGTH).optional();
/** Validation messages emitted by `timeRangesSchema`. */
const TIME_RANGE_ISSUE_MESSAGES = {
    REVERSED: 'endTime must be after startTime',
    OVERLAP: 'timeRanges must not contain overlapping periods',
};
/**
 * Adds an issue for any range whose `endTime` is not strictly after its
 * `startTime`. Times are `HH:mm` 24h strings, so lexicographic comparison
 * matches chronological order.
 */
function refineReversedRanges(params) {
    const { ranges, ctx } = params;
    for (const [index, range] of ranges.entries()) {
        if (range.endTime <= range.startTime) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: TIME_RANGE_ISSUE_MESSAGES.REVERSED,
                path: [index, 'endTime'],
            });
        }
    }
}
/**
 * Adds an issue when two ranges overlap. Input is not assumed pre-sorted: a copy
 * is sorted by `startTime` first, then each range's start must be `>=` the
 * previous range's end. Times are `HH:mm` 24h strings (lexicographic = chrono).
 */
function refineOverlappingRanges(params) {
    const { ranges, ctx } = params;
    const sorted = [...ranges].sort((a, b) => a.startTime.localeCompare(b.startTime));
    let previousEndTime = null;
    for (const range of sorted) {
        if (previousEndTime !== null && range.startTime < previousEndTime) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                message: TIME_RANGE_ISSUE_MESSAGES.OVERLAP,
                path: [],
            });
            return;
        }
        previousEndTime = range.endTime;
    }
}
/**
 * The working periods of a slot. When present, the server treats this as the
 * source of truth and derives `startTime`/`endTime`/`workedMinutes` from it
 * (envelope + summed durations), so a multi-period day type (e.g. "Long" with a
 * midday break) round-trips without flattening to a single window.
 *
 * Beyond non-emptiness, the array rejects reversed ranges (`endTime <=
 * startTime`) and any overlap between ranges (checked after sorting a copy by
 * `startTime`, since input is not assumed ordered).
 */
exports.timeRangesSchema = zod_1.z
    .array(slot_types_1.assistantSlotTypeTimeRangeSchema)
    .min(1)
    .superRefine((ranges, ctx) => {
    refineReversedRanges({ ranges, ctx });
    refineOverlappingRanges({ ranges, ctx });
});
exports.overrideSlotMovePayloadSchema = zod_1.z
    .object({
    changeType: zod_1.z.literal('MOVED'),
    date: zod_1.z.string().optional(),
    slotTypeId: zod_1.z.string().optional(),
    startTime: zod_1.z.string().optional(),
    endTime: zod_1.z.string().optional(),
    workedMinutes: zod_1.z.number().optional(),
    timeRanges: exports.timeRangesSchema.optional(),
    note: slotNoteSchema,
})
    .refine((payload) => payload.date !== undefined ||
    payload.slotTypeId !== undefined ||
    payload.startTime !== undefined ||
    payload.endTime !== undefined ||
    payload.workedMinutes !== undefined ||
    payload.timeRanges !== undefined, {
    message: 'A MOVED override must change at least one of date, slotTypeId, startTime, endTime, workedMinutes or timeRanges',
});
exports.overrideSlotReassignPayloadSchema = zod_1.z.object({
    changeType: zod_1.z.literal('REASSIGNED'),
    assistantId: zod_1.z.string(),
    note: slotNoteSchema,
});
exports.overrideSlotCancelPayloadSchema = zod_1.z.object({
    changeType: zod_1.z.literal('CANCELLED'),
    note: slotNoteSchema,
});
exports.overrideSlotAbsencePayloadSchema = zod_1.z.object({
    changeType: zod_1.z.literal('ABSENCE'),
    absenceType: absence_enums_1.absenceTypeSchema,
    note: slotNoteSchema,
});
exports.overrideSlotApiPayloadSchema = zod_1.z.union([
    exports.overrideSlotMovePayloadSchema,
    exports.overrideSlotReassignPayloadSchema,
    exports.overrideSlotCancelPayloadSchema,
    exports.overrideSlotAbsencePayloadSchema,
]);
exports.assistantPlannedSlotApiResponseSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    assistantId: zod_1.z.string().uuid(),
    officeId: zod_1.z.string().uuid(),
    date: zod_1.z.string(),
    slotTypeId: zod_1.z.string().uuid(),
    startTime: zod_1.z.string(),
    endTime: zod_1.z.string(),
    workedMinutes: zod_1.z.number().int(),
    // The slot's working periods. Optional for backward compatibility with older
    // payloads; the API always sends it (single-range envelope when not split).
    timeRanges: exports.timeRangesSchema.optional(),
    source: enums_1.slotSourceSchema,
    status: enums_1.plannedSlotStatusSchema,
    generatedFromWeek: zod_1.z.enum(['A', 'B']).nullable(),
});
exports.createSlotApiPayloadSchema = zod_1.z.object({
    assistantId: zod_1.z.string().uuid(),
    officeId: zod_1.z.string().uuid(),
    date: zod_1.z.string(),
    slotTypeId: zod_1.z.string().uuid(),
    startTime: zod_1.z.string(),
    endTime: zod_1.z.string(),
    workedMinutes: zod_1.z.number().int().min(0),
    /**
     * The slot's working periods. When present, the server derives
     * `startTime`/`endTime`/`workedMinutes` from it (so a multi-period day type is
     * preserved).
     */
    timeRanges: exports.timeRangesSchema.optional(),
});
